import React, { useState } from 'react';
import { Menu, MenuItem, Button, Snackbar, alpha } from '@mui/material';
import { Link2, Share2, Facebook, Twitter, Instagram } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { trackShared } from '../../utils/analytics';

// Import additional social icons if needed
// Note: Lucide React doesn't have a Reddit icon, so we're using a custom SVG
const RedditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" fill="#FF4500" />
    <circle cx="8" cy="10" r="1.5" fill="white" />
    <circle cx="16" cy="10" r="1.5" fill="white" />
    <path d="M8.5,15.5 C10.5,17.5 13.5,17.5 15.5,15.5" stroke="white" strokeLinecap="round" />
    <path d="M18.5,11c0.83,0,1.5-0.67,1.5-1.5s-0.67-1.5-1.5-1.5" stroke="#FF4500" />
    <path d="M18.5,8.5C18.5,6.57,16.93,5,15,5" stroke="#FF4500" />
    <path d="M5.5,11c-0.83,0-1.5-0.67-1.5-1.5s0.67-1.5,1.5-1.5" stroke="#FF4500" />
  </svg>
);

const ShareCircleButton = ({ circle, darkMode = false }) => {
  const { id } = useParams();
  const circleId = id || circle?.id;
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  // Define colors for dark mode
  const colors = {
    buttonBg: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'white',
    buttonText: darkMode ? 'rgba(255, 255, 255, 0.9)' : '#0066cc',
    buttonHover: darkMode ? 'rgba(255, 255, 255, 0.15)' : '#f8f9fa',
    buttonBorder: darkMode ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
    buttonShadow: darkMode ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
    menuBg: darkMode ? '#1F2937' : 'white',
    menuText: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'inherit',
    menuBorder: darkMode ? 'rgba(75, 85, 99, 0.3)' : 'transparent',
    menuItemHover: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    snackbarBg: darkMode ? '#374151' : undefined,
    snackbarText: darkMode ? 'rgba(255, 255, 255, 0.9)' : undefined
  };

  // Format sharing content with null checks
  const formatShareContent = () => {
    if (!circle) return null;

    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    const shareUrl = `${baseUrl}/circle/${circleId}`;

    // Format features text if available
    const featuresText = circle.features?.length > 0 
      ? ` Features: ${Array.isArray(circle.features) 
          ? circle.features.map(f => typeof f === 'string' ? f : f.short_description).join(', ')
          : ''}`
      : '';

    return {
      url: shareUrl,
      title: circle.name ? `Join ${circle.name} - ${circle.carrier || 'Mobile'} Circle` : `Join our ${circle.carrier || 'Mobile'} Circle`,
      description: `Join ${circle.name} Circle! Save money on your ${circle.carrier || 'mobile'} plan. $${circle.monthlyPayment || 0}/month, ${circle.available_slots || 0} spots left.${featuresText}`,
      features: featuresText
    };
  };

  const shareContent = formatShareContent();

  const handleClick = (event) => {
    if (!circle || !circleId) {
      console.error('Missing required data for sharing');
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCopyLink = async () => {
    try {
      if (!shareContent) {
        console.error('Share content not available');
        return;
      }

      const textToCopy = `${shareContent.title}\n${shareContent.description}\n\nJoin here: ${shareContent.url}`;
      await navigator.clipboard.writeText(textToCopy);
      trackShared(circleId, 'copy_link');
      setShowCopySuccess(true);
      handleClose();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSocialShare = (platform) => {
    if (!shareContent) {
      console.error('Share content not available');
      return;
    }

    let platformUrl;
    switch (platform) {
      case 'facebook':
        platformUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareContent.url)}&quote=${encodeURIComponent(`${shareContent.title}\n${shareContent.description}`)}`;
        break;
      case 'twitter':
        // Twitter has a character limit, so we'll use a shorter format
        const twitterText = `${shareContent.title}\n${shareContent.description}`;
        platformUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareContent.url)}`;
        break;
      case 'reddit':
        // Reddit sharing
        platformUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareContent.url)}&title=${encodeURIComponent(shareContent.title)}`;
        break;
      case 'instagram':
        handleCopyLink();
        alert('Link copied! You can now share it on Instagram');
        handleClose();
        return;
      default:
        console.error('Unsupported platform:', platform);
        return;
    }

    // Open share window in the center of the screen
    const width = 600;
    const height = 400;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    trackShared(circleId, platform);
    window.open(
      platformUrl,
      'share-dialog',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
    );
    handleClose();
  };

  if (!circle || !circleId) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<Share2 size={16} />}
        onClick={handleClick}
        sx={{
          color: colors.buttonText,
          backgroundColor: colors.buttonBg,
          borderRadius: '20px',
          boxShadow: colors.buttonShadow,
          padding: '6px 16px',
          textTransform: 'none',
          fontSize: '14px',
          fontWeight: 500,
          border: `1px solid ${colors.buttonBorder}`,
          '&:hover': {
            backgroundColor: colors.buttonHover
          }
        }}
      >
        Share Circle
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: darkMode ? 4 : 3,
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: '200px',
            bgcolor: colors.menuBg,
            color: colors.menuText,
            border: `1px solid ${colors.menuBorder}`,
            '& .MuiMenuItem-root': {
              '&:hover': {
                bgcolor: colors.menuItemHover
              }
            }
          }
        }}
      >
        <MenuItem onClick={handleCopyLink} sx={{ gap: 1.5, py: 1 }}>
          <Link2 size={18} />
          Copy Link
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('facebook')} sx={{ gap: 1.5, py: 1 }}>
          <Facebook size={18} color="#1877F2" />
          Share on Facebook
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('twitter')} sx={{ gap: 1.5, py: 1 }}>
          <Twitter size={18} color="#1DA1F2" />
          Share on Twitter
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('reddit')} sx={{ gap: 1.5, py: 1 }}>
          <div style={{ color: '#FF4500' }}>
            <RedditIcon />
          </div>
          Share on Reddit
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('instagram')} sx={{ gap: 1.5, py: 1 }}>
          <Instagram size={18} color="#E4405F" />
          Share on Instagram
        </MenuItem>
      </Menu>

      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2000}
        onClose={() => setShowCopySuccess(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: colors.snackbarBg,
            color: colors.snackbarText
          }
        }}
      />
    </>
  );
};

export default ShareCircleButton;